import axios from 'axios';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AuthUserData,
  getResourcePermissions,
  ResourcePermissions,
} from '../utils/permission-utils';
import { subscribePermissionsRefresh } from '../utils/permission-refresh';

interface UseResourcePermissionsResult extends ResourcePermissions {
  authUserData: AuthUserData | null;
  isLoadingPermissions: boolean;
}

export const useResourcePermissions = (resourceRoute: string): UseResourcePermissionsResult => {
  const [authUserData, setAuthUserData] = useState<AuthUserData | null>(null);
  const [isLoadingPermissions, setIsLoadingPermissions] = useState(true);

  const fetchAuthUser = useCallback(async (isMounted = true) => {
      if (isMounted) {
        setIsLoadingPermissions(true);
      }

      try {
        const { data } = await axios.get('user');

        if (isMounted) {
          setAuthUserData(data?.data ?? null);
        }
      } catch (error) {
        if (isMounted) {
          setAuthUserData(null);
        }
      } finally {
        if (isMounted) {
          setIsLoadingPermissions(false);
        }
      }
    }, []);

  useEffect(() => {
    let isMounted = true;

    const runFetch = () => {
      fetchAuthUser(isMounted);
    };

    runFetch();

    const unsubscribe = subscribePermissionsRefresh(() => {
      runFetch();
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [fetchAuthUser]);

  const permissions = useMemo(
    () => getResourcePermissions(authUserData, resourceRoute),
    [authUserData, resourceRoute]
  );

  return {
    ...permissions,
    authUserData,
    isLoadingPermissions,
  };
};
